import React, { useCallback, useEffect, useRef, useState } from "react";
import { assetUrl } from "@/lib/api";
import {
  ArrowLeft, ArrowRight, ArrowUp, Folder, FolderOpen, FolderPlus, Home, Pencil, Trash2, Layers, Sparkles,
  HardDrive, ExternalLink, ScanText, Loader2, Download, AlertTriangle, FileText, Image as ImageIcon,
  FileSpreadsheet, File as FileIcon,
} from "lucide-react";

export const KIND_ICON = { pdf: FileText, image: ImageIcon, excel: FileSpreadsheet, word: FileText, text: FileText, other: FileIcon };
export const fmtSize = (b) => {
  if (b == null) return "";
  if (b >= 1e9) return (b / 1e9).toFixed(2) + " GB";
  if (b >= 1e6) return (b / 1e6).toFixed(1) + " MB";
  if (b >= 1e3) return (b / 1e3).toFixed(0) + " KB";
  return b + " B";
};

const stop = (e) => e.stopPropagation();
const HOVER = "opacity-0 group-hover:opacity-100 [@media(hover:none)]:opacity-100";

// Click selects, double-click opens; one tap opens on touch screens.
export function useOpenGesture() {
  const touch = useRef(false);
  const last = useRef(0);
  return useCallback((onSelect, onOpen) => {
    const open = () => {
      const now = Date.now();
      if (touch.current && now - last.current < 450) return;
      last.current = now;
      onOpen();
    };
    return {
      onPointerDown: (e) => { touch.current = e.pointerType === "touch"; },
      onClick: (e) => { e.stopPropagation(); if (touch.current) open(); else onSelect(); },
      onDoubleClick: (e) => { e.stopPropagation(); if (!touch.current) open(); },
    };
  }, []);
}

const itemCls = (sel) => `group flex items-center gap-3 rounded-lg px-3 py-2 cursor-default select-none touch-manipulation border transition-colors ${sel ? "bg-[#003E87]/[0.07] border-[#003E87]/30" : "border-transparent hover:bg-slate-50 hover:border-slate-200"}`;
const iconBtn = "text-slate-400 hover:text-[#003E87] disabled:opacity-40";

export function RenameInput({ value, onDone, onCancel, className, testid }) {
  return (
    <input autoFocus defaultValue={value} onFocus={(e) => e.target.select()} onClick={stop} onDoubleClick={stop} onPointerDown={stop}
      onBlur={(e) => onDone(e.target.value)}
      onKeyDown={(e) => { e.stopPropagation(); if (e.key === "Enter") e.currentTarget.blur(); if (e.key === "Escape") onCancel(); }}
      className={`min-w-0 w-full border border-[#003E87] rounded px-1 py-0.5 ${className || "text-xs"}`} data-testid={testid} />
  );
}

export function AddressBar({ crumbs, canBack, canFwd, canUp, onBack, onFwd, onUp, onCrumb }) {
  const nav = "p-1.5 rounded text-slate-500 hover:bg-slate-100 hover:text-[#003E87] disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-slate-500 shrink-0";
  const bar = useRef(null);
  const where = crumbs.map((c) => c.key).join("/");
  useEffect(() => { if (bar.current) bar.current.scrollLeft = bar.current.scrollWidth; }, [where]);
  return (
    <div className="flex items-center gap-1 min-w-0" data-testid="doc-address-bar">
      <button className={nav} disabled={!canBack} onClick={onBack} title="Back (Alt+Left)" data-testid="doc-nav-back"><ArrowLeft className="w-4 h-4" /></button>
      <button className={nav} disabled={!canFwd} onClick={onFwd} title="Forward (Alt+Right)" data-testid="doc-nav-forward"><ArrowRight className="w-4 h-4" /></button>
      <button className={nav} disabled={!canUp} onClick={onUp} title="Up one level (Backspace)" data-testid="doc-nav-up"><ArrowUp className="w-4 h-4" /></button>
      <div ref={bar} className="flex-1 min-w-0 flex items-center border border-slate-200 rounded px-2 py-1 overflow-x-auto whitespace-nowrap text-xs" data-testid="doc-breadcrumb">
        {crumbs.map((c, i) => {
          const last = i === crumbs.length - 1;
          return (
            <React.Fragment key={c.key}>
              {i > 0 && <span className="text-slate-300 px-1">/</span>}
              <button onClick={() => onCrumb(c)} disabled={last} data-testid={`doc-crumb-${i}`}
                className={`px-1 py-0.5 rounded ${last ? "text-slate-900 font-medium" : "text-slate-500 hover:bg-slate-100 hover:text-[#003E87]"}`}>
                {c.label}
              </button>
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
}

export function FolderPane({ folders, active, pathIds, readOnly, canDelete, onOpen, onNew, renamingFolder, setRenamingFolder, onRename, onDelete }) {
  const gesture = useOpenGesture();
  const [sel, setSel] = useState(null);
  const open = new Set(pathIds);
  const kids = (pid) => folders.filter((f) => (f.parent_id || null) === (pid || null));
  const rows = [];
  const walk = (pid, depth) => kids(pid).forEach((f) => { rows.push({ f, depth }); if (open.has(f.folder_id)) walk(f.folder_id, depth + 1); });
  walk(null, 0);
  const rowCls = (isActive, isSel) => `group flex items-center gap-1.5 pr-2 py-1 rounded cursor-default select-none touch-manipulation ${isActive ? "bg-[#003E87]/10 text-[#003E87] font-medium" : isSel ? "bg-slate-100 text-slate-800" : "hover:bg-slate-50 text-slate-700"}`;
  return (
    <div className="hidden md:block w-60 shrink-0 border-r border-slate-200 bg-white p-3 overflow-auto" data-testid="doc-folder-pane">
      <div className="flex items-center justify-between mb-2">
        <div className="text-[11px] uppercase tracking-wide text-slate-400">Folders</div>
        {!readOnly && <button onClick={onNew} className="text-[#003E87] hover:bg-slate-50 rounded p-1" title="New folder" data-testid="new-folder-btn"><FolderPlus className="w-4 h-4" /></button>}
      </div>
      <div className={rowCls(active === "", sel === "")} style={{ paddingLeft: 8 }} {...gesture(() => setSel(""), () => onOpen(""))} data-testid="folder-root">
        <Home className="w-3.5 h-3.5 shrink-0" /><span className="text-xs">All documents</span>
      </div>
      {rows.map(({ f, depth }) => (
        <div key={f.folder_id} className={rowCls(active === f.folder_id, sel === f.folder_id)} style={{ paddingLeft: 8 + (depth + 1) * 14 }}
          {...gesture(() => setSel(f.folder_id), () => onOpen(f.folder_id))} data-testid={`folder-${f.folder_id}`}>
          {open.has(f.folder_id) ? <FolderOpen className="w-3.5 h-3.5 shrink-0 text-[#c79f00]" /> : <Folder className="w-3.5 h-3.5 shrink-0 text-[#c79f00]" />}
          {renamingFolder === f.folder_id ? (
            <RenameInput value={f.name} onDone={(v) => onRename(f, v)} onCancel={() => setRenamingFolder(null)} testid={`folder-rename-${f.folder_id}`} />
          ) : <span className="text-xs truncate flex-1" title={f.name}>{f.name}</span>}
          {renamingFolder !== f.folder_id && (
            <span className="flex items-center gap-1 shrink-0" onClick={stop} onDoubleClick={stop}>
              {!readOnly && <button onClick={() => setRenamingFolder(f.folder_id)} className={`text-slate-300 hover:text-[#003E87] ${HOVER}`} title="Rename folder" data-testid={`folder-rename-btn-${f.folder_id}`}><Pencil className="w-3 h-3" /></button>}
              {canDelete && <button onClick={() => onDelete(f)} className={`text-slate-300 hover:text-rose-600 ${HOVER}`} title="Delete folder" data-testid={`folder-delete-btn-${f.folder_id}`}><Trash2 className="w-3 h-3" /></button>}
            </span>
          )}
        </div>
      ))}
      <div className="text-[10px] text-slate-400 mt-3 px-2" data-testid="doc-folder-pane-hint">Double-click a folder to open it.</div>
    </div>
  );
}

export function FolderItem({ f, subCount, selected, gestureProps, renaming, readOnly, canDelete, onRenameStart, onRename, onRenameCancel, onDelete }) {
  return (
    <div className={itemCls(selected)} {...gestureProps} data-testid={`item-folder-${f.folder_id}`}>
      <div className="w-10 h-10 flex items-center justify-center shrink-0"><Folder className="w-7 h-7 text-[#c79f00] fill-[#F4C400]/40" /></div>
      <div className="min-w-0 flex-1">
        {renaming ? <RenameInput value={f.name} className="text-sm" onDone={onRename} onCancel={onRenameCancel} testid={`item-folder-rename-${f.folder_id}`} />
          : <div className="text-sm text-slate-800 truncate" title={f.name}>{f.name}</div>}
        <div className="text-[11px] text-slate-400">File folder{subCount ? ` · ${subCount} folder${subCount === 1 ? "" : "s"} inside` : ""}</div>
      </div>
      <div className="flex items-center gap-2 shrink-0" onClick={stop} onDoubleClick={stop}>
        {!readOnly && <button onClick={onRenameStart} className={`${iconBtn} ${HOVER}`} title="Rename (F2)" data-testid={`item-folder-rename-btn-${f.folder_id}`}><Pencil className="w-4 h-4" /></button>}
        {canDelete && <button onClick={onDelete} className={`text-slate-300 hover:text-rose-600 ${HOVER}`} title="Delete folder" data-testid={`item-folder-delete-${f.folder_id}`}><Trash2 className="w-4 h-4" /></button>}
      </div>
    </div>
  );
}

function DocMeta({ d }) {
  const naming = d.ai_status === "processing";
  const ocring = d.ocr_status === "processing";
  return (
    <div className="text-[11px] text-slate-400 flex items-center gap-2 flex-wrap">
      <span className="uppercase">{d.kind}</span>
      {d.superseded_by_round ? <span className="px-1 rounded bg-slate-100 text-slate-500 normal-case" data-testid={`doc-superseded-${d.document_id}`} title={`An earlier round; the current round is ${d.superseded_by_round}`}>superseded {d.round || d.superseded_by_round}</span> : null}
      {d.archived ? <span className="px-1 rounded bg-slate-100 text-slate-500" data-testid={`doc-archived-${d.document_id}`}>archived</span> : null}
      <span>{fmtSize(d.size)}</span>
      {d.page_count ? <span>{d.page_count} page{d.page_count === 1 ? "" : "s"}</span> : null}
      {d.sheet_count ? <span className="text-[#003E87] flex items-center gap-0.5" data-testid={`doc-sheet-count-${d.document_id}`}><Layers className="w-3 h-3" /> {d.sheet_count} sheet{d.sheet_count === 1 ? "" : "s"}</span> : null}
      {d.ai_status === "done" ? <span className="text-emerald-600 flex items-center gap-0.5"><Sparkles className="w-3 h-3" /> named</span> : null}
      {naming ? <span className="text-[#8a6d00] flex items-center gap-0.5"><Loader2 className="w-3 h-3 animate-spin" /> naming {d.ai_progress || 0}%</span> : null}
      {ocring ? <span className="text-[#8a6d00] flex items-center gap-0.5"><Loader2 className="w-3 h-3 animate-spin" /> reading text {d.ocr_progress || 0}%</span> : null}
      {!ocring && d.needs_ocr ? <span className="text-amber-600 flex items-center gap-0.5"><ScanText className="w-3 h-3" /> not searchable</span> : null}
      {d.annotate_only ? <span className="text-[#003E87] flex items-center gap-0.5" title="Opens in Takeoff for annotations & comments (measuring is for PDF/image sheets)"><Pencil className="w-3 h-3" /> annotate</span> : null}
      {d.convert_error ? <span className="text-rose-500 flex items-center gap-0.5" title={d.convert_error} data-testid={`doc-convert-error-${d.document_id}`}><AlertTriangle className="w-3 h-3" /> can't open — save as .docx/.xlsx</span> : null}
      {d.storage_backend === "drive" ? <span className="text-[#003E87] flex items-center gap-0.5" data-testid={`doc-drive-badge-${d.document_id}`} title="Stored in your Google Shared Drive"><HardDrive className="w-3 h-3" /> Drive</span> : null}
    </div>
  );
}

const HistoryLink = ({ d, onHistory, className = "" }) => (d.history_count && onHistory ? (
  <button onClick={(e) => { e.stopPropagation(); onHistory(d); }} onDoubleClick={stop} className={`text-[11px] font-medium text-[#003E87] hover:underline whitespace-nowrap ${className}`}
    title="Earlier versions of this document" data-testid={`doc-history-${d.document_id}`}>History ({d.history_count})</button>
) : null);

export function DocItem({ d, header, selected, gestureProps, renaming, readOnly, canDelete, onRenameStart, onRename, onRenameCancel, onDownload, onOpenDrive, onDelete, onAiName, onOcr, onHistory }) {
  const Icon = KIND_ICON[d.kind] || FileIcon;
  const canAi = d.kind === "pdf" || d.kind === "image";
  const [thumbErr, setThumbErr] = useState(false);
  const cls = header ? "group flex items-center gap-3 rounded-lg px-3 py-2 border border-slate-200 bg-slate-50/60" : itemCls(selected);
  return (
    <div className={cls} {...(gestureProps || {})} data-testid={header ? `doc-header-${d.document_id}` : `doc-${d.document_id}`}>
      {d.cover_sheet_id && !thumbErr ? (
        <img src={assetUrl(`/api/sheets/${d.cover_sheet_id}/image?thumb=1`)} alt="" loading="lazy" draggable={false} className="w-10 h-10 rounded object-cover bg-slate-100 border border-slate-200 shrink-0" onError={() => setThumbErr(true)} />
      ) : <div className="w-10 h-10 flex items-center justify-center shrink-0"><Icon className="w-5 h-5 text-[#003E87]" /></div>}
      <div className="min-w-0 flex-1">
        {renaming && !readOnly ? <RenameInput value={d.name} className="text-sm" onDone={onRename} onCancel={onRenameCancel} testid={`doc-rename-${d.document_id}`} />
          : <div className="text-sm text-slate-800 truncate" title={d.name}>{d.name}</div>}
        <DocMeta d={d} />
      </div>
      <div className="flex items-center gap-2 shrink-0" onClick={stop} onDoubleClick={stop}>
        <HistoryLink d={d} onHistory={onHistory} className="mr-1" />
        {d.storage_backend === "drive" && <button onClick={() => onOpenDrive(d)} className={iconBtn} title="Open in Google Drive" data-testid={`doc-open-drive-${d.document_id}`}><ExternalLink className="w-4 h-4" /></button>}
        {!readOnly && canAi && d.needs_ocr && <button onClick={() => onOcr(d)} disabled={d.ocr_status === "processing"} className={iconBtn} title="Extract text (OCR) so this becomes searchable" data-testid={`doc-ocr-${d.document_id}`}><ScanText className="w-4 h-4" /></button>}
        {!readOnly && canAi && <button onClick={() => onAiName(d)} disabled={d.ai_status === "processing"} className="text-slate-400 hover:text-[#F4C400] disabled:opacity-40" title="Auto-name sheets from title block (AI)" data-testid={`doc-ainame-${d.document_id}`}><Sparkles className="w-4 h-4" /></button>}
        {!readOnly && onRename && <button onClick={onRenameStart} className={`${iconBtn} ${header ? "" : HOVER}`} title="Rename (F2)" data-testid={`doc-rename-btn-${d.document_id}`}><Pencil className="w-4 h-4" /></button>}
        <button onClick={() => onDownload(d)} className={iconBtn} title="Open / download the file" data-testid={`doc-download-${d.document_id}`}><Download className="w-4 h-4" /></button>
        {canDelete && <button onClick={() => onDelete(d)} className={`hover:text-rose-600 ${header ? "text-slate-500" : `text-slate-300 ${HOVER}`}`} title="Remove" data-testid={`doc-delete-${d.document_id}`}><Trash2 className="w-4 h-4" /></button>}
      </div>
    </div>
  );
}

export function SheetItem({ s, selected, gestureProps, renaming, readOnly, snippet, onRenameStart, onRename, onRenameCancel, onOpen }) {
  const [err, setErr] = useState(false);
  return (
    <div className={itemCls(selected)} {...gestureProps} data-testid={`sheet-${s.sheet_id}`}>
      {!err ? <img src={assetUrl(`/api/sheets/${s.sheet_id}/image?thumb=1`)} alt="" loading="lazy" draggable={false} className="w-12 h-10 rounded object-cover bg-slate-100 border border-slate-200 shrink-0" onError={() => setErr(true)} />
        : <div className="w-12 h-10 flex items-center justify-center shrink-0"><Layers className="w-4 h-4 text-slate-300" /></div>}
      <div className="min-w-0 flex-1">
        {renaming && !readOnly ? <RenameInput value={s.name} className="text-sm" onDone={onRename} onCancel={onRenameCancel} testid={`sheet-name-${s.sheet_id}`} /> : (
          <div className="text-sm text-slate-800 truncate" title={s.name}>
            {s.sheet_number ? <span className="font-mono text-[#003E87] mr-2">{s.sheet_number}</span> : null}{s.name}
          </div>
        )}
        <div className="text-[11px] text-slate-400 flex items-center gap-2">
          <span>Sheet{s.page_number ? ` ${s.page_number}` : ""}</span>
          {s.ai_named ? <span className="text-emerald-600 flex items-center gap-0.5"><Sparkles className="w-3 h-3" /> AI-named</span> : null}
        </div>
        {snippet ? <div className="text-[11px] text-slate-400 mt-0.5 leading-snug">{snippet}</div> : null}
      </div>
      <div className="flex items-center gap-2 shrink-0" onClick={stop} onDoubleClick={stop}>
        {!readOnly && onRenameStart && <button onClick={onRenameStart} className={`${iconBtn} ${HOVER}`} title="Rename (F2)" data-testid={`sheet-rename-btn-${s.sheet_id}`}><Pencil className="w-4 h-4" /></button>}
        {onOpen && <button onClick={onOpen} className="text-[11px] text-[#003E87] hover:underline" data-testid={`open-sheet-${s.sheet_id}`}>Open in Takeoff</button>}
      </div>
    </div>
  );
}

const tileCls = (sel) => `group text-left border rounded-lg overflow-hidden select-none touch-manipulation cursor-default transition-colors ${sel ? "border-[#003E87] ring-2 ring-[#003E87]/20" : "border-slate-200 hover:border-[#003E87]/60"}`;

export function FolderTile({ f, selected, gestureProps }) {
  return (
    <div className={tileCls(selected)} {...gestureProps} data-testid={`grid-folder-${f.folder_id}`}>
      <div className="aspect-[4/3] bg-[#F4C400]/10 flex items-center justify-center"><Folder className="w-12 h-12 text-[#c79f00] fill-[#F4C400]/40" /></div>
      <div className="px-2 py-1.5 border-t border-slate-100"><div className="text-xs text-slate-700 truncate" title={f.name}>{f.name}</div><div className="text-[11px] text-slate-400">File folder</div></div>
    </div>
  );
}

export function DocTile({ d, selected, gestureProps, onHistory }) {
  const Icon = KIND_ICON[d.kind] || FileIcon;
  const [err, setErr] = useState(false);
  return (
    <div className={tileCls(selected)} {...gestureProps} data-testid={`grid-doc-${d.document_id}`}>
      <div className="aspect-[4/3] bg-slate-100 flex items-center justify-center overflow-hidden">
        {d.cover_sheet_id && !err ? <img src={assetUrl(`/api/sheets/${d.cover_sheet_id}/image?thumb=1`)} alt="" loading="lazy" draggable={false} className="w-full h-full object-cover" onError={() => setErr(true)} />
          : <Icon className="w-10 h-10 text-[#003E87]/60" />}
      </div>
      <div className="px-2 py-1.5 border-t border-slate-100">
        <div className="text-xs text-slate-700 truncate" title={d.name}>{d.name}</div>
        <div className="text-[11px] text-slate-400 flex items-center justify-between gap-1">
          <span>{d.sheet_count ? `${d.sheet_count} sheet${d.sheet_count === 1 ? "" : "s"}` : (d.kind || "file").toUpperCase()}</span>
          <HistoryLink d={d} onHistory={onHistory} />
        </div>
      </div>
    </div>
  );
}

export function SheetTile({ s, selected, gestureProps }) {
  return (
    <div className={tileCls(selected)} {...gestureProps} data-testid={`grid-sheet-${s.sheet_id}`}>
      <div className="aspect-[4/3] bg-slate-100 flex items-center justify-center overflow-hidden">
        <img src={assetUrl(`/api/sheets/${s.sheet_id}/image?thumb=1`)} alt="" loading="lazy" draggable={false} className="w-full h-full object-cover" onError={(e) => { e.currentTarget.style.visibility = "hidden"; }} />
      </div>
      <div className="px-2 py-1.5 border-t border-slate-100">
        {s.sheet_number ? <div className="text-[11px] font-mono text-[#003E87] truncate">{s.sheet_number}</div> : null}
        <div className="text-xs text-slate-700 truncate" title={s.name}>{s.name}</div>
      </div>
    </div>
  );
}
